import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import MeshLines from '../components/MeshLines';

const ContactPage: React.FC = () => {
  const { language, setLanguage, t, isRTL } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    teamSize: '',
    message: '',
  });

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In production, send to API. For now, show success message.
    setSubmitted(true);
  };

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className="nu-landing"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/images/hero-bg.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >

      {/* ========== 1. NAVBAR ========== */}
      <nav className={`nu-navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nu-container nav-inner">
          <Link className="nu-brand" to="/">
            <img src="/nuagent-logo.png" alt="Nuagent" className="brand-logo" />
          </Link>

          <div className="nu-nav-links d-none d-md-flex">
            <Link to="/#product">{t('nav_product', 'Product')}</Link>
            <Link to="/#features">{t('nav_features', 'Features')}</Link>
            <Link to="/#pricing">{t('nav_pricing', 'Pricing')}</Link>
          </div>

          <div className="nu-nav-actions">
            <div className="lang-toggle">
              <button className={language === 'en' ? 'active' : ''} onClick={() => setLanguage('en')}>EN</button>
              <button className={language === 'ar' ? 'active' : ''} onClick={() => setLanguage('ar')}>AR</button>
            </div>
            <Link to="/login" className="nu-btn-ghost d-none d-sm-inline-flex">{t('nav_login', 'Sign In')}</Link>
            <Link to="/register" className="nu-btn-primary">
              <MeshLines horizontals={4} verticals={14} spacing={8} />
              <span className="btn-content">{t('nav_start', 'Get Started')} <span className="btn-arrow">{isRTL ? '←' : '→'}</span></span>
            </Link>
          </div>
        </div>
      </nav>

      {/* ========== 2. HERO BANNER ========== */}
      <section className="contact-hero">
        <div className="nu-container">
          <h1 className="contact-hero-title">{t('contact_hero_title', 'CONTACT SALES.')}</h1>
          <p className="contact-hero-subtitle">
            {t('contact_hero_subtitle_prefix', 'Thanks for your interest in')}
            <img src="/nuagent-logo.png" alt="Nuagent" className="inline-logo" />
            {t('contact_hero_subtitle_suffix', '. Fill out the form and our team will reach out ASAP.')}
          </p>
        </div>
      </section>

      {/* ========== 3. CONTACT FORM SECTION ========== */}
      <section className="contact-form-section">
        <div className="nu-container contact-form-grid">

          {/* Left column: Form */}
          <div className="contact-form-left">
            {submitted ? (
              <div className="contact-success">
                <div className="contact-success-icon">&#10003;</div>
                <h2>{t('contact_success', 'Thank you! Our team will be in touch shortly.')}</h2>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Row 1 */}
                <div className="contact-form-row">
                  <div className="contact-form-group">
                    <label className="contact-label mono">
                      <i className="bi bi-person"></i> {t('contact_first_name', 'FIRST NAME')}
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder={t('contact_first_name_ph', 'John')}
                      className="contact-input"
                      required
                    />
                  </div>
                  <div className="contact-form-group">
                    <label className="contact-label mono">
                      <i className="bi bi-person"></i> {t('contact_last_name', 'LAST NAME')}
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder={t('contact_last_name_ph', 'Doe')}
                      className="contact-input"
                      required
                    />
                  </div>
                </div>

                {/* Row 2 */}
                <div className="contact-form-row">
                  <div className="contact-form-group">
                    <label className="contact-label mono">
                      <i className="bi bi-envelope"></i> {t('contact_email', 'WORK EMAIL')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('contact_email_ph', 'john@company.com')}
                      className="contact-input"
                      required
                    />
                  </div>
                  <div className="contact-form-group">
                    <label className="contact-label mono">
                      <i className="bi bi-building"></i> {t('contact_company', 'COMPANY NAME')}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder={t('contact_company_ph', 'Acme Inc.')}
                      className="contact-input"
                      required
                    />
                  </div>
                </div>

                {/* Row 3 */}
                <div className="contact-form-row full-width">
                  <div className="contact-form-group">
                    <label className="contact-label mono">
                      <i className="bi bi-people"></i> {t('contact_team_size', 'HOW MANY EMPLOYEES?')}
                    </label>
                    <select
                      name="teamSize"
                      value={formData.teamSize}
                      onChange={handleChange}
                      className="contact-input contact-select"
                      required
                    >
                      <option value="">{t('contact_team_select', 'Select team size')}</option>
                      <option value="1-9">1-9</option>
                      <option value="10-50">10-50</option>
                      <option value="51-200">51-200</option>
                      <option value="200+">200+</option>
                    </select>
                  </div>
                </div>

                {/* Row 4 */}
                <div className="contact-form-row full-width">
                  <div className="contact-form-group">
                    <label className="contact-label mono">
                      <i className="bi bi-chat-text"></i> {t('contact_message', 'WHAT WOULD BE HELPFUL FOR US TO KNOW?')}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact_message_ph', 'Tell us about your business and what you are looking to achieve...')}
                      className="contact-input contact-textarea"
                      rows={5}
                    />
                  </div>
                </div>

                {/* Row 5 */}
                <button type="submit" className="nu-btn-primary contact-submit-btn">
                  <MeshLines horizontals={5} verticals={26} spacing={10} />
                  <span className="btn-content">{t('contact_submit', 'Contact Us')}</span>
                </button>
              </form>
            )}
          </div>

          {/* Right column: Decorative */}
          <div className="contact-form-right">
            <div className="contact-decorative-card">
              [ Decorative Image ]
            </div>
          </div>

        </div>
      </section>

      {/* ========== 4. FOOTER ========== */}
      <footer className="nu-footer">
        <div className="nu-container">
          <div className="footer-top">
            <div className="footer-brand">
              <img src="/nuagent-logo.png" alt="Nuagent" className="footer-logo" />
              <p>{t('footer_tagline', 'Making business management simple, smart, and secure.')}</p>
            </div>
            <div className="footer-links">
              <div className="link-col">
                <h5>{t('footer_product', 'Product')}</h5>
                <Link to="/#features">{t('nav_features', 'Features')}</Link>
                <Link to="/#pricing">{t('nav_pricing', 'Pricing')}</Link>
                <a href="/docs">{t('footer_api_docs', 'API Docs')}</a>
                <Link to="/#security">{t('footer_security', 'Security')}</Link>
              </div>
              <div className="link-col">
                <h5>{t('footer_company', 'Company')}</h5>
                <a href="/about">{t('footer_about', 'About')}</a>
                <a href="mailto:hello@nuagent.net">{t('footer_contact', 'Contact')}</a>
                <a href="/careers">{t('footer_careers', 'Careers')}</a>
                <a href="/blog">{t('footer_blog', 'Blog')}</a>
              </div>
              <div className="link-col">
                <h5>{t('footer_legal', 'Legal')}</h5>
                <a href="/privacy">{t('footer_privacy', 'Privacy')}</a>
                <a href="/terms">{t('footer_terms', 'Terms')}</a>
                <a href="/sla">{t('footer_sla', 'SLA')}</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Nuagent. {t('footer_rights', 'All rights reserved.')}</p>
            <p className="footer-powered">{t('footer_powered', 'Powered by Nuwas')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
